import { memo, useEffect, useState } from 'react'
import { MetricsWidgetProps } from '@/api/Metrics/Types/MetricsDto'
import { Chart } from '@/components/Chart/Chart'
import { Loading } from '@/components/Loading'
import { MetricsWidgetConfig, WIDGET_CONFIG } from '@/components/Metrics/config'
import {
  getTotalValueByMetricsType,
  processChartData,
} from '@/components/Metrics/helpers/processChartData'
import { EmptyStateSkeleton } from '@/components/ui/empty-state-skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useApiClient } from '@/utils/api/ApiClient'
import { MetricsApiClient } from '@/utils/api/metricsApi'
import { useCallbackWithForceRefresh } from '@/utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from '@/utils/api/useFetch'
import { API_DATE_FORMAT, LAST_30_DAYS_INCLUDING_TODAY } from '@/utils/date'
import { formatLargeNumber, formatNumberWithCommas } from '@/utils/formatNumber'
import { getCurrencySymbol } from '@/utils/getCurrencySymbol'
import { format } from 'date-fns'

export const MetricsWidget = memo(
  ({
    type,
    introducingBrokerIds,
    countryIds,
    customDateRange,
    intervalType,
  }: MetricsWidgetProps) => {
    const config = WIDGET_CONFIG[type as keyof typeof WIDGET_CONFIG] as MetricsWidgetConfig
    const metricsApiClient = useApiClient(MetricsApiClient)

    const [dateRange, setDateRange] = useState<[Date, Date]>(() =>
      customDateRange
        ? [new Date(customDateRange[0]), new Date(customDateRange[1])]
        : LAST_30_DAYS_INCLUDING_TODAY
    )

    const from = format(dateRange[0], API_DATE_FORMAT)
    const to = format(dateRange[1], API_DATE_FORMAT)

    const { callback: fetchData } = useCallbackWithForceRefresh(() => {
      return config.fetchData(metricsApiClient, {
        from,
        to,
        introducingBrokerIds,
        countryIds,
        intervalType,
      })
    }, [from, to, introducingBrokerIds, countryIds, intervalType, config])

    const { data, isLoading } = useFetchOne(fetchData)
    const metricsData = processChartData(data, config.category)

    const isIntegerMetric =
      config.category === 'newClientsMetrics' ||
      config.category === 'openedTAMetrics' ||
      config.category === 'ftdMetrics'
    const decimalPlaces = isIntegerMetric ? 0 : 2

    const shortValue = formatLargeNumber(
      getTotalValueByMetricsType(data, config.category),
      decimalPlaces
    )

    const fullValue = formatNumberWithCommas(getTotalValueByMetricsType(data, config.category), {
      minDecimalPlaces: decimalPlaces,
      maxDecimalPlaces: decimalPlaces,
    })

    const displayValue = config.unit ? `${getCurrencySymbol(config.unit)}${shortValue}` : shortValue
    const tooltipValue = config.unit ? `${getCurrencySymbol(config.unit)}${fullValue}` : fullValue

    useEffect(() => {
      if (customDateRange) {
        setDateRange([new Date(customDateRange[0]), new Date(customDateRange[1])])
      } else {
        setDateRange(LAST_30_DAYS_INCLUDING_TODAY)
      }
    }, [customDateRange])

    return (
      <Loading isLoading={isLoading} modalView>
        {data ? (
          <div className='flex flex-col h-full min-h-0 p-3 rounded-lg'>
            <div className='flex flex-col mb-2'>
              <div className='flex items-center justify-between'>
                <div className='text-[1.125rem] font-semibold text-muted-foreground'>
                  {config.title}
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      render={<div className='text-2xl font-bold text-primary text-center' />}
                    >
                      {displayValue}
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{tooltipValue}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className='text-xs text-muted-foreground self-end mb-2'>Total for Period</div>
            </div>
            <div className='flex flex-col flex-1 min-h-0'>
              <div className='w-full h-full flex-1 min-h-50'>
                <Chart data={metricsData} decimalPlaces={decimalPlaces} dateRange={dateRange} />
              </div>
            </div>
          </div>
        ) : (
          <div className='flex flex-col h-full min-h-0 p-3 rounded-lg'>
            <div className='flex flex-col flex-1 min-h-0'>
              <EmptyStateSkeleton title='No data to display' className='h-full' />
            </div>
          </div>
        )}
      </Loading>
    )
  }
)
